import React, { useMemo } from "react";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";

const getString = (value, fallback = "-") => {
	if (value === null || value === undefined) return fallback;
	if (typeof value === "string" && value.trim() === "") return fallback;
	return value;
};

const toNumber = (value) => {
	if (value === null || value === undefined) return NaN;
	if (typeof value === "number") return value;
	const parsed = Number(String(value).replace(/[%\s,]/g, ""));
	return Number.isFinite(parsed) ? parsed : NaN;
};

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString() : "-");
const formatPercent = (value) => (Number.isFinite(value) ? `${value.toFixed(0)}%` : "-");

const buildRowsFromBarcodes = (barcodes, nodBarcodesLength) => {
	if (!Array.isArray(barcodes) || barcodes.length === 0) return [];
	if (typeof barcodes[0] === "string") {
		return [
			{
				category: "Barcodes",
				option: "Selected",
				count: barcodes.length,
				discountPct: NaN,
				exclusionCount: nodBarcodesLength,
			},
		];
	}

	return barcodes.map((row, index) => ({
		id: row.id ?? index,
		category: getString(row.category ?? row.Category, "Category"),
		option: getString(row.option ?? row.Option ?? row.option_name ?? row.optionName, "Option"),
		optionCount: toNumber(row.optionCount ?? row.option_count),
		count: toNumber(row.count ?? row.Count ?? row.qty ?? row.Qty ?? row.quantity),
		discountPct: toNumber(row.discountPct ?? row.discount_percent ?? row.discount ?? row["Discount(%)"]),
		exclusionCount: toNumber(row.exclusionCount ?? row.exclusion_count ?? row.exclusion ?? row.excludedCount),
	}));
};

function SelectedProductSummary({ barcodes = [], nod_barcdes = [] }) {
	const rows = useMemo(() => buildRowsFromBarcodes(barcodes, nod_barcdes?.length || 0), [barcodes, nod_barcdes]);

	const summary = useMemo(() => {
		if (!rows.length) {
			return {
				categoryCount: 0,
				optionCount: 0,
				qtyAffected: 0,
				discountPct: NaN,
				exclusionCount: Array.isArray(nod_barcdes) ? nod_barcdes.length : 0,
			};
		}

		const categoryCount = new Set(rows.map((row) => row.category)).size;
		const optionCountCandidates = rows.map((row) => row.optionCount).filter(Number.isFinite);
		const optionCount = optionCountCandidates.length ? optionCountCandidates.reduce((sum, value) => sum + value, 0) : rows.length;
		const qtyAffected = rows
			.map((row) => row.count)
			.filter(Number.isFinite)
			.reduce((sum, value) => sum + value, 0);

		const discountCandidates = rows
			.map((row) => ({
				value: row.discountPct,
				weight: Number.isFinite(row.count) ? row.count : 1,
			}))
			.filter((row) => Number.isFinite(row.value));

		const discountPct = discountCandidates.length
			? discountCandidates.reduce((sum, row) => sum + row.value * row.weight, 0) /
				discountCandidates.reduce((sum, row) => sum + row.weight, 0)
			: NaN;

		const exclusionCountCandidates = rows.map((row) => row.exclusionCount).filter(Number.isFinite);
		const exclusionCount = exclusionCountCandidates.length
			? exclusionCountCandidates.reduce((sum, value) => sum + value, 0)
			: Array.isArray(nod_barcdes)
				? nod_barcdes.length
				: 0;

		return { categoryCount, optionCount, qtyAffected, discountPct, exclusionCount };
	}, [rows, nod_barcdes]);

	return (
		<Box display="flex" gap={2} flexWrap="wrap" width={"100%"} pl={2} flexDirection={"row"}>
			<Box>
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Category</TableCell>
								<TableCell>Option</TableCell>
								<TableCell align="right">Count</TableCell>
								<TableCell align="right">Discount%</TableCell>
								<TableCell align="right">Exclusion count</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.length ? (
								rows.map((row) => (
									<TableRow key={row.id ?? `${row.category}-${row.option}`}>
										<TableCell>{row.category}</TableCell>
										<TableCell>{row.option}</TableCell>
										<TableCell align="right">{formatNumber(row.count)}</TableCell>
										<TableCell align="right">{formatPercent(row.discountPct)}</TableCell>
										<TableCell align="right">{formatNumber(row.exclusionCount)}</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<Typography variant="body2" color="text.secondary">
											No product summary available
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Paper>
			</Box>
			<Box>
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "fit-content" }}>
					<Stack spacing={1}>
						<Typography variant="subtitle2" color="text.secondary">
							Summary
						</Typography>
						<Typography variant="body2">Category Count: {formatNumber(summary.categoryCount)}</Typography>
						<Typography variant="body2">Option Count: {formatNumber(summary.optionCount)}</Typography>
						<Typography variant="body2">Qty affected: {formatNumber(summary.qtyAffected)}</Typography>
						<Typography variant="body2">Discount(%): {formatPercent(summary.discountPct)}</Typography>
						<Typography variant="body2">Exclusion Count: {formatNumber(summary.exclusionCount)}</Typography>
					</Stack>
				</Paper>
			</Box>
		</Box>
	);
}

export default SelectedProductSummary;
